import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse, IconButton, Stack, Typography } from '@mui/material';
import { Blacklist_New, BlacklistTypes } from 'features/co-pilot/company-blacklists/company-blacklist.types';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiSwitch from 'features/ui/base-components/pai-switch';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import BlacklistItemTable from './blacklist-item-table';

interface ItemProps {
  blacklist: Blacklist_New;
  checked: boolean;
  loading: boolean;
  selectionLoading: boolean;

  handleSelectedBlacklistsChange(): void;

  handleOpenEditBlacklistItemsDialog(): void;

  handleOpenEditBlacklistNameDialog(): void;

  handleOpenDeleteBlacklistDialog(): void;

  handleRemoveSingleBlacklistItem(identifier: string): () => void;
}

const BlacklistItem: React.FC<ItemProps> = ({
  blacklist,
  checked,
  loading,
  selectionLoading,
  handleSelectedBlacklistsChange,
  handleOpenEditBlacklistItemsDialog,
  handleOpenEditBlacklistNameDialog,
  handleOpenDeleteBlacklistDialog,
  handleRemoveSingleBlacklistItem,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  useEffect(() => {
    if (_.isEmpty(blacklist.items)) {
      setExpanded(false);
    }
  }, [blacklist.items]);

  return (
    <div className={classes.blacklistItem}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <PaiSwitch checked={checked} disabled={selectionLoading} onClick={handleSelectedBlacklistsChange} />
          <Typography className={classes.regularText}>{_.truncate(blacklist.name || '...', { length: 35 })}</Typography>
        </Stack>
        <Stack direction={'row'} alignItems={'center'} gap={3}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography className={classes.greyText}>|</Typography>
            <Typography className={classes.greyText}>
              {`${blacklist.items?.length ?? 0} ${t(blacklist.blacklistType === BlacklistTypes.COMPANY ? 'companies' : 'leads')} ${t('addedToBlacklist')}`}
            </Typography>
          </Stack>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <IconButton className={classes.iconButton} size={'small'} onClick={handleOpenEditBlacklistNameDialog}>
              <EditIcon />
            </IconButton>
            <IconButton className={classes.iconButton} size={'small'} onClick={handleOpenDeleteBlacklistDialog}>
              <DeleteIcon />
            </IconButton>
          </Stack>
          <IconButton className={cx(classes.expandButton, expanded ? 'expanded' : undefined)} size={'small'} onClick={handleToggleExpand}>
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse className={classes.itemCollapse} in={expanded} timeout={0} unmountOnExit={true}>
        <BlacklistItemTable
          blacklistType={blacklist.blacklistType}
          blacklistItems={blacklist.items}
          loading={loading}
          handleOpenEditBlacklistItemsDialog={handleOpenEditBlacklistItemsDialog}
          handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem}
        />
      </Collapse>
    </div>
  );
};

interface Props {
  blacklists: Blacklist_New[];
  loading: boolean;
  selectedLocalBlacklists: string[];
  blacklistSyncLoading: boolean;

  handleSelectedBlacklistsChange(blacklist: Blacklist_New): () => void;

  handleOpenAddLocalBlacklistDialog(): void;

  handleOpenEditBlacklistItemsDialog(blacklist: Blacklist_New): () => void;

  handleOpenEditBlacklistNameDialog(blacklist: Blacklist_New): () => void;

  handleOpenDeleteBlacklistDialog(blacklist: Blacklist_New): () => void;

  handleRemoveSingleBlacklistItem(blacklist: Blacklist_New): (identifier: string) => () => void;
}

const LocalBlacklistsTable: React.FC<Props> = ({
  blacklists,
  loading,
  selectedLocalBlacklists,
  blacklistSyncLoading,
  handleSelectedBlacklistsChange,
  handleOpenAddLocalBlacklistDialog,
  handleOpenEditBlacklistItemsDialog,
  handleOpenEditBlacklistNameDialog,
  handleOpenDeleteBlacklistDialog,
  handleRemoveSingleBlacklistItem,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const isChecked = (blacklist: Blacklist_New) => {
    return _.includes(selectedLocalBlacklists, blacklist._id);
  };

  return (
    <Stack className={classes.blacklistTable} gap={1}>
      {blacklists.map((blacklist) => (
        <BlacklistItem
          key={blacklist._id}
          blacklist={blacklist}
          checked={isChecked(blacklist)}
          loading={loading}
          selectionLoading={blacklistSyncLoading}
          handleSelectedBlacklistsChange={handleSelectedBlacklistsChange(blacklist)}
          handleOpenEditBlacklistItemsDialog={handleOpenEditBlacklistItemsDialog(blacklist)}
          handleOpenEditBlacklistNameDialog={handleOpenEditBlacklistNameDialog(blacklist)}
          handleOpenDeleteBlacklistDialog={handleOpenDeleteBlacklistDialog(blacklist)}
          handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem(blacklist)}
        />
      ))}
      <div className={classes.linkContainer}>
        <PaiLink text={t('createNewBlacklist')} size={'lg'} action={handleOpenAddLocalBlacklistDialog} />
      </div>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  blacklistTable: {
    overflow: 'auto',
  },
  blacklistItem: {
    width: '100%',
    padding: '8px 32px',
    background: '#FFF',
    borderRadius: '12px',
  },
  expandButton: {
    padding: '1px',
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
      transition: '225ms',
    },
    '&.expanded': {
      '& svg': {
        transform: 'rotate(180deg)',
      },
    },
  },
  iconButton: {
    padding: '4px',
    '& svg': {
      fontSize: theme.typography.pxToRem(16),
    },
  },
  itemCollapse: {
    paddingTop: '16px',
  },
  regularText: {
    fontSize: theme.typography.pxToRem(15),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  linkContainer: {
    padding: '0 32px',
  },
}));

export default LocalBlacklistsTable;
